import React, { Fragment } from 'react'

import { CmsPage, RenderCmsComponent } from 'bloomreach-experience-react-sdk'
import getConfig from 'next/config'
import NextRouter from 'next/router'

import { WebPage } from '@grandvision/analytics'
import {
  BloomreachApi,
  DefaultPageProps,
  DynamicRoutes,
  getPageSeo,
  HeadTags,
} from '@grandvision/bloomreach'
import {
  AlgoliaConfigProvider,
  AlgoliaConfigProviderProps,
  AlgoliaSearch,
  ConnectedProductListing,
  fetchAlgoliaConfig,
  SearchPageHeader,
} from '@grandvision/search'
import { Locales, useIntl } from '@grandvision/state'

import componentDefinitions from '../cms/component-definitions'
import Layout from '../components/Layout'
import { createAlgoliaConfig } from '../util/algolia'

const { publicRuntimeConfig } = getConfig()
const selfUrl = publicRuntimeConfig.selfUrl || ''
const cmsUrls = publicRuntimeConfig.bloomreach.cmsUrls

interface Props extends DefaultPageProps {
  searchQuery?: string
  algoliaConfig: AlgoliaConfigProviderProps
}

const SearchPage = ({ algoliaConfig, asPath, pageModel, request, searchQuery }: Props) => {
  const { getLabel } = useIntl()

  if (!pageModel) {
    console.error('No page model defined for search page!')
    return null
  }

  const seoData: BloomreachApi.SeoData = getPageSeo(pageModel)
  const pageTitle = searchQuery
    ? `${getLabel('search.results.title')} '${searchQuery}'`
    : getLabel('search.pageTitle')
  const amountOfHitsLabel = getLabel('search.results.amount.title')

  return (
    <CmsPage
      cmsUrls={cmsUrls}
      pageModel={pageModel}
      request={request}
      componentDefinitions={componentDefinitions}
    >
      {() => (
        <AlgoliaConfigProvider {...algoliaConfig} NextRouter={NextRouter}>
          <Layout asPath={asPath} pageModel={pageModel}>
            <HeadTags title={pageTitle} noIndex={true} noFollow={true} />
            <RenderCmsComponent path="main/top-container" />

            <div className="search-page">
              <DynamicRoutes pageModel={pageModel}>
                {({ formatProductUrl }) => (
                  <AlgoliaSearch>
                    {(stateResults) =>
                      stateResults && (
                        <Fragment>
                          <SearchPageHeader
                            pageTitle={pageTitle}
                            hitTitle={`${stateResults.searchResults.nbHits} ${amountOfHitsLabel}`}
                          />
                          <ConnectedProductListing
                            formatProductUrl={formatProductUrl}
                            staticsUrl={publicRuntimeConfig.grandvision.staticsUrl}
                          />
                        </Fragment>
                      )
                    }
                  </AlgoliaSearch>
                )}
              </DynamicRoutes>
            </div>

            <RenderCmsComponent path="main/bottom-container" />

            <WebPage
              url={selfUrl + asPath}
              pageType="Information"
              name={seoData.title}
              description={seoData.description}
            />
          </Layout>
        </AlgoliaConfigProvider>
      )}
    </CmsPage>
  )
}

SearchPage.getInitialProps = async ({ asPath, query }) => {
  const { pageModel, request, preview, search } = query as any
  const algoliaConfig = await fetchAlgoliaConfig({
    ...createAlgoliaConfig(asPath),
    ruleContexts: ['search'],
    locale: Locales[query.locale],
  })
  return { asPath, pageModel, preview, request, searchQuery: search.q, algoliaConfig }
}

export default SearchPage
